/**
 * Owner: Masa Bayu
 * File: DedicatedLibraryScreen.tsx
 * Date:Time: 2026-10-17 17:45
 * Description: Dedicated reader bookshelf — resume-first shelf wired to the real library
 *   model (R-02 honest access). Profile palette, purpose-built layout, themed controls.
 */

import { useSyncExternalStore } from 'react';
import type { CSSProperties } from 'react';
import { ensureDemoReadingStarted } from '../../library/demo-reading-continuity';
import {
  bumpDemoLibraryRevision,
  getDemoLibraryRevision,
  requestDemoReaderLaunch,
  requestDemoResume,
  setDemoLibraryQuery,
  subscribeDemoLibraryRevision,
} from '../../library/demo-library-store';
import { createLibraryModel } from '../../library/shared/createLibraryModel';
import type { LibraryShelfCard } from '../../library/shared/library-types';
import './dedicated.css';

const COVER_TONES = ['#db2777', '#7c3aed', '#0891b2', '#c2410c', '#15803d', '#4f46e5'];

const FILTER_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'reading', label: 'Reading' },
  { key: 'completed', label: 'Completed' },
  { key: 'update_available', label: 'Updates' },
  { key: 'blocked', label: 'Blocked' },
] as const;

const SORT_OPTIONS = [
  { key: 'recent', label: 'Recent' },
  { key: 'title', label: 'Title' },
  { key: 'author', label: 'Author' },
  { key: 'progress', label: 'Progress' },
] as const;

function coverStyle(card: LibraryShelfCard, index: number): CSSProperties {
  if (card.coverUrl !== null) {
    return { backgroundImage: `url("${card.coverUrl}")` };
  }
  return { '--tone': COVER_TONES[index % COVER_TONES.length] } as CSSProperties;
}

export interface DedicatedLibraryScreenProps {
  readonly onNavigate: (path: string) => void;
  readonly deepLinkPath?: string | undefined;
}

export function DedicatedLibraryScreen({ onNavigate, deepLinkPath }: DedicatedLibraryScreenProps) {
  // §458b — geran hub masuk secara tak segerak selepas first paint; rak wajib
  // dilukis semula apabila stor menaikkan revision, bukan hanya apabila pengguna klik.
  useSyncExternalStore(subscribeDemoLibraryRevision, getDemoLibraryRevision);
  const model = createLibraryModel(deepLinkPath);

  const bump = () => {
    bumpDemoLibraryRevision();
  };

  const openItem = (publicationId: string) => {
    ensureDemoReadingStarted(publicationId);
    requestDemoReaderLaunch(publicationId);
    bump();
    onNavigate(`/read/${encodeURIComponent(publicationId)}`);
  };

  const continueItem = (publicationId: string) => {
    requestDemoResume(publicationId);
    requestDemoReaderLaunch(publicationId);
    bump();
    onNavigate(`/read/${encodeURIComponent(publicationId)}`);
  };

  return (
    <div className="ddx">
      <header>
        <p className="ddx__eyebrow">QXRead · Personal</p>
        <h2 className="ddx__title">My Library</h2>
        <p className="ddx__lede">
          Your bookshelf · resume-first · entitled titles only. Continue where you left off or open
          something new.
        </p>
      </header>

      <div className="ddx__toolbar">
        <input
          className="ddx__search"
          type="search"
          placeholder="Search your library"
          aria-label="Search library"
          value={model.query}
          onChange={(event) => {
            setDemoLibraryQuery({ query: event.target.value });
            bump();
          }}
        />
        <div className="ddx__chips" role="group" aria-label="Filter library">
          {FILTER_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              className="ddx__chip"
              data-on={model.filter === option.key}
              onClick={() => {
                setDemoLibraryQuery({ filter: option.key });
                bump();
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
        <select
          className="ddx__select"
          aria-label="Sort library"
          value={model.sort}
          onChange={(event) => {
            setDemoLibraryQuery({ sort: event.target.value as typeof model.sort });
            bump();
          }}
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {model.continueReading.length > 0 ? (
        <section aria-labelledby="ddx-continue">
          <div className="ddx__head">
            <h3 id="ddx-continue">Continue Reading</h3>
            <span className="ddx__count">{String(model.continueReading.length)} in progress</span>
          </div>
          <div className="ddx__shelf">
            {model.continueReading.map((card, index) => (
              <article className="ddx__book" key={`c-${card.item.libraryItemId}`}>
                <span className="ddx__cover" style={coverStyle(card, index)}>
                  {card.coverUrl === null ? card.item.title : null}
                </span>
                <h4>{card.item.title}</h4>
                <p className="ddx__authors">{card.authors.join(', ')}</p>
                <p className="ddx__progress">
                  <span
                    className="ddx__progress-bar"
                    style={
                      {
                        '--fill': `${String(card.item.progress.percentComplete)}%`,
                      } as CSSProperties
                    }
                  />
                  <em>{card.progressLabel}</em>
                </p>
                <button
                  type="button"
                  className="ddx__cta ddx__cta--solid"
                  onClick={() => {
                    continueItem(card.item.publicationId);
                  }}
                >
                  Continue
                </button>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <section aria-labelledby="ddx-shelf">
        <div className="ddx__head">
          <h3 id="ddx-shelf">Library</h3>
          <span className="ddx__count">{String(model.itemCount)} titles</span>
        </div>
        {model.empty ? (
          <div className="ddx__empty">
            <h3>Your library is empty</h3>
            <p>Purchased and entitled titles appear here. Discover the marketplace to start.</p>
            <button
              type="button"
              className="ddx__cta ddx__cta--solid"
              onClick={() => {
                onNavigate('/discover');
              }}
            >
              Discover titles
            </button>
          </div>
        ) : (
          <div className="ddx__shelf">
            {model.shelf.length === 0 ? (
              <p className="ddx__lede">No titles match this filter.</p>
            ) : (
              model.shelf.map((card, index) => (
                <article className="ddx__book" key={card.item.libraryItemId}>
                  <span className="ddx__cover" style={coverStyle(card, index)}>
                    {card.coverUrl === null ? card.item.title : null}
                  </span>
                  <h4>{card.item.title}</h4>
                  <p className="ddx__authors">{card.authors.join(', ')}</p>
                  <p className="ddx__progress">
                    <span
                      className="ddx__progress-bar"
                      style={
                        {
                          '--fill': `${String(card.item.progress.percentComplete)}%`,
                        } as CSSProperties
                      }
                    />
                    <em>{card.progressLabel}</em>
                  </p>
                  <button
                    type="button"
                    className="ddx__cta"
                    disabled={!card.canOpen}
                    onClick={() => {
                      if (card.item.progress.percentComplete > 0) {
                        continueItem(card.item.publicationId);
                      } else {
                        openItem(card.item.publicationId);
                      }
                    }}
                  >
                    {card.accessLabel}
                  </button>
                </article>
              ))
            )}
          </div>
        )}
      </section>
    </div>
  );
}
